<template>
    <b-card header-tag="header" footer-tag="footer" class="mt-8">
        <template v-slot:header>
            <p class="mb-0" style="font-size: 21px">Listado de Pedidos Clientes</p>
        </template>
        <b-row class="pb-2">
            <b-col cols="12" md="12">
                <div class="text-right">
                    <b-button-group>
                        <!-- Botón Descargar Excel -->
                        <b-button v-if="user.isMaster" variant="info" class="text-white mr-2" @click="downloadExcel">
                            <b-icon icon="download"></b-icon> Descargar Plantilla
                        </b-button>

                        <!-- Botón Sincronizar Pagos (visible solo si el usuario es 'master') -->
                        <b-button v-if="user.isMaster" variant="success" class="text-white" @click="uploadExcel">
                            <b-icon icon="upload"></b-icon> Subir pedidos
                        </b-button>
                    </b-button-group>
                </div>
            </b-col>
        </b-row>

        <!-- Alerta para pedidos seleccionados -->
        <b-row class="pb-2" v-if="selectedOrders.length > 0 && checkOwnOdooPermission()">
            <b-col cols="12">
                <b-alert show variant="info" class="d-flex justify-content-between align-items-center">
                    <span>{{ selectedOrders.length }} pedido(s) seleccionado(s)</span>
                    <div>
                        <b-button 
                            variant="outline-secondary" 
                            size="sm"
                            class="mr-2"
                            @click="clearSelection">
                            Limpiar
                        </b-button>
                        <b-button 
                            variant="primary" 
                            size="sm"
                            @click="sendSelectedOrders"
                            :disabled="sendingBatch">
                            <b-spinner small v-if="sendingBatch"></b-spinner>
                            {{ sendingBatch ? 'Enviando...' : 'Enviar a Odoo' }}
                        </b-button>
                    </div>
                </b-alert>
            </b-col>
        </b-row>

        <b-card-text>
            <b-table 
                responsive
                :fields="computedFields" 
                striped hover 
                :items="pedidos"
                :tbody-tr-class="getRowClass">
                
                <!-- Columna de selección -->
                <template v-slot:cell(select)="{ item }" v-if="checkOwnOdooPermission()">
                    <b-form-checkbox 
                        v-model="selectedOrders" 
                        :value="item.id"
                        :disabled="item.is_sent_to_odoo === 1">
                    </b-form-checkbox>
                </template>

                <template v-slot:cell(id)="data">
                    {{ data.item.id }}
                </template>
                <template v-slot:cell(nombre_usuario)="data">
                    {{ data.item.nombre_usuario }}
                </template>
                <template v-slot:cell(pedido_proveedor_id)="data">
                     <b v-if="canOrdersProv && data.item.pedido_proveedor_id"><router-link :to="{name: 'InfoPedidoProveedor', params:{ id: data.item.pedido_proveedor_id}}">#{{data.item.pedido_proveedor_id}}</router-link> </b>
                </template>
                <template v-slot:cell(created_at)="data">
                    {{ data.item.created_at }}
                </template>
                <template v-slot:cell(nombre_cliente)="data">
                    {{ data.item.nombre_cliente }}
                </template>
                <template v-slot:cell(documento_cliente)="data">
                    {{ data.item.documento_cliente }}
                </template>
                <template v-slot:cell(nombre_departamento_pedido)="data">
                    {{ data.item.nombre_departamento_pedido }}
                </template>
                <template v-slot:cell(nombre_ciudad_pedido)="data">
                    {{ data.item.nombre_ciudad_pedido }}
                </template>
                <template class="text-center" v-slot:cell(is_sent_to_odoo)="data">
                    <b-badge :variant="data.item.is_sent_to_odoo === 1 ? 'success' : 'danger'">
                        {{ data.item.is_sent_to_odoo === 1 ? 'Sí' : 'No' }}
                    </b-badge>
                </template>
                <template v-slot:cell(total_commission_on_sales)="data" v-if="canSeeSalesComission">
                    $ {{numberFormat(data.item.total_commission_on_sales) }}
                </template>
                <template v-slot:cell(total_precio)="data">
                    $ {{numberFormat(data.item.total_precio) }}
                </template>
                <!-- Nueva columna para guías PDF -->
                <template v-slot:cell(guide_pdf_url)="data">
                    <!-- Si hay PDF, mostrar enlace -->
                    <div v-if="data.item.guide_pdf_url">
                        <a :href="data.item.guide_pdf_url" v-b-tooltip.hover title="Ver Guía" target="_blank" class="btn btn-sm btn-info text-white">
                            <b-icon icon="file-earmark" aria-hidden="true"></b-icon> Ver
                        </a>
                    </div>
                    <!-- Si no hay PDF, mostrar botón de subir (solo para usuario master) -->
                    <div v-else>
                        <b-button
                            size="sm"
                            variant="success"
                            @click="showUploadPdfModal(data.item.id, data.item)"
                            class="d-flex align-items-center justify-content-between"
                            >
                            <b-icon icon="upload" aria-hidden="true" class="mr-2"></b-icon>
                            <span>Cargar</span>
                        </b-button>
                    </div>
                </template>
                <template v-slot:cell(actions)="{ item }">
                    <b-button size="sm" variant="info" v-b-tooltip.hover title="Detalle Pedido" class="text-white" 
                    :to="{name: 'InfoPedido', params:{ id: item.id}}">
                        <b-icon icon="search"></b-icon>
                    </b-button>
                </template>
            </b-table>
            <template v-if="pedidos.length < 1">
                <b-alert show variant="danger">
                    Lo sentimos, no se han encontrado resultados para su busqueda.
                </b-alert>
            </template>
        </b-card-text>
        <template v-slot:footer>
            <div class="mt-3">
                <b-row>                            
                    <b-col cols="12" md="4">
                        <b-pagination 
                            size="md" 
                            pills 
                            :total-rows="paginator.totalPage" 
                            v-model="paginator.page" 
                            :per-page="1" 
                            @input="changePage" 
                            first-number 
                            last-number>
                        </b-pagination>
                    </b-col>
                    <b-col cols="12" sm="2">
                        <b-form-select @change="updatePageSize()" v-model="paginator.pageSize" :options="pageSize" ></b-form-select>
                    </b-col>
                </b-row>
            </div>
        </template>
    </b-card>
</template>

<script>
import { mapState } from 'vuex'
export default {
    name: "PedidosTable",
    props: {
        pedidos: {
            type: Array
        },
        paginator: {
            type: Object
        },
        pageSize: {
            type: Array
        },
        canSeeSalesComission: {
            type: Boolean
        },
        canOrdersProv: {
            type: Boolean
        },
        odooIstances: {
            type: Array
        }
    },
    computed: {
        computedFields() {
            // Crear una copia del array fields para manipularlo
            let fieldsToShow = [...this.fields];
            
            // Filtrar la columna select basado en permisos de Odoo
            fieldsToShow = fieldsToShow.filter((field) => {
                if (field.key === 'select') {
                    return this.checkOwnOdooPermission();
                }
                
                if (field.requiresPermission) {
                    if (field.key === "total_commission_on_sales") {
                        return this.canSeeSalesComission;
                    }
                    if (field.key === "guide_pdf_url") {
                        // Obtener los permisos de la empresa desde localStorage
                        const companyPermissions = localStorage.getItem('empresa_permissions');
                        
                        // Verificar si la empresa tiene el permiso de subir PDF
                        if (companyPermissions && companyPermissions.includes('can_upload_guide_order')) {
                            // Si la empresa tiene el permiso, entonces verificar si el usuario es master
                            return this.user.isMaster === 1;
                        }
                        return false; // Si la empresa no tiene el permiso, no mostrar el campo
                    }
                    if (field.key === "is_sent_to_odoo") {
                        return this.showLabelStatusForOdoo();
                    }

                    if (field.key === "pedido_proveedor_id") {
                        return this.canOrdersProv;
                    }

                } else {
                    return true;
                }
            });
            
            return fieldsToShow;
        },
        ...mapState('auth', ['user']),
    },
    data(){
        return{
            selectedOrders: [],
            sendingBatch: false,
            isSentToOdoo: false,
            fields: [
                { key: 'select', label: '', sortable: false}, // Nueva columna para checkbox
                { key: 'id', label: '#', sortable: true},
                { key: 'nombre_usuario', label: 'Realizado Por', sortable: true},
                { key: 'pedido_proveedor_id', label: 'Pedido Proveedor', sortable: true, requiresPermission: true},
                { key: 'created_at', label: 'Fecha', sortable: true},
                { key: 'nombre_cliente', label: 'Cliente', sortable: true},
                { key: 'documento_cliente', label: 'Nit', sortable: true},
                { key: 'nombre_departamento_pedido', label: 'Depto', sortable: true},
                { key: 'nombre_ciudad_pedido', label: 'Ciudad', sortable: true},
                { key: 'is_sent_to_odoo', label: 'Enviado Odoo', sortable: true, requiresPermission: true},
                { key: 'total_commission_on_sales', label: 'Comisión de venta', sortable: true, requiresPermission: true},
                { key: 'total_precio', label: 'Precio Total', sortable: true},
                // Nueva columna para guía PDF (solo visible para usuarios master)
                { key: 'guide_pdf_url', label: 'Guía', sortable: false, requiresPermission: true},
                { key: 'actions', label: 'Acciones'},
            ]
        }
    },
    methods: {
        updatePageSize(){
            /* the page sizee return the current page to initial because we can't know if the current page have items asigned!! */
            this.paginator.page = 1;
            /* emit the page size update to get the new list of items*/
            this.$emit('get-pedidos');
        },
        changePage(){
            this.$emit('change-page', this.paginator.page);            
        },
        downloadExcel(){
            this.$emit('download-excel');
        },
        uploadExcel(){
            this.$emit('upload-excel');
        },
        // Nuevo método para mostrar el modal de subir PDF
        showUploadPdfModal(orderId, orderData) {
            // Este método emite un evento para que el componente padre (Pedidos.vue) muestre el modal
            this.$emit('show-guide-upload-modal', orderId, orderData);
        },
        
       
        
        sendSelectedOrders() {
            if (this.selectedOrders.length === 0) {
                this.$bvToast.toast('Selecciona al menos un pedido', { 
                    variant: 'warning',
                    title: 'Advertencia'
                });
                return;
            }
            
            // Emitir evento al componente padre con los IDs seleccionados
            // NO cambiar sendingBatch aquí, el modal se encarga del estado
            this.$emit('send-batch-orders', this.selectedOrders);
        },
        
        // Método para limpiar selección
        clearSelection() {
            this.selectedOrders = [];
        },
        
        // Método para finalizar el envío (llamado desde el padre)
        finishBatchSending() {
            this.sendingBatch = false;
            this.clearSelection();
        },
        
        // Método para iniciar el estado de envío (llamado desde el padre)
        startBatchSending() {
            this.sendingBatch = true;
        },
        
        // Validación de permisos para Odoo
        checkOwnOdooPermission() {
            
            const companyPermissions = localStorage.getItem('empresa_permissions');

            const hasActiveInstance = this.odooIstances && this.odooIstances.some(
                instance => instance.status === true
            );

            const result = (
                companyPermissions &&
                companyPermissions.includes('can_use_module_own_odoo') &&
                this.user.isMaster === 1 &&
                hasActiveInstance
            );
            
            return result;
        },
        
        // Método para agregar clase CSS a las filas
        getRowClass(item, type) {
            if (!item || type !== 'row') return '';
            if (item.is_already_sent === 1) return 'is-sent';
            return '';
        },

        showLabelStatusForOdoo() {
            const companyPermissions = localStorage.getItem('empresa_permissions');

            const result = (
                companyPermissions &&
                companyPermissions.includes('can_use_module_own_odoo') &&
                this.user.isMaster === 1
            );
            
            return result;
        },
    }
};
</script>

<style scoped>
/* Estilo para pedidos ya enviados */
.table >>> tr.is-sent {
    background-color: #f8f9fa;
    opacity: 0.7;
}

.table >>> tr.is-sent td {
    color: #6c757d;
}

/* Estilo para checkboxes deshabilitados */
.table >>> .custom-control-input:disabled ~ .custom-control-label::before {
    background-color: #e9ecef;
    border-color: #adb5bd;
}
</style>